import { HocuspocusProvider } from "@hocuspocus/provider";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import * as Y from "yjs";
import { authClient } from "@/lib/auth";
import avatarUrl, { taskFileUrl } from "@/lib/image-urls";
import { trpcClient } from "@/lib/trpc";
import "./tiptap-editor.css";
import { isTiptapDocumentEmpty } from "@orsane/util/text";
import Emoji from "@tiptap/extension-emoji";
import Mention from "@tiptap/extension-mention";
import type { EditorView } from "@tiptap/pm/view";
import axios from "axios";
import { Bold, Heading, Italic, List, ListOrdered, Pilcrow, Strikethrough } from "lucide-react";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import { toast } from "sonner";
import { useThrottle } from "@/hooks/useThrottle";
import { cn, promiseSleep } from "@/lib/utils";
import { useChangeTaskProperty } from "@/mutations/task-mutations";
import { Button } from "../ui/button";
import ImageLightbox, { type ImageLightboxSlide } from "./ImageLightbox";
import { OrsaneMediaNode } from "./OrsaneMediaNode";
import { mentionListConfiguration, mentionListExtension } from "./userSuggestion";

function BubbleSeparator() {
    return <div className="h-6 w-px bg-neutral-200 mx-1" />;
}

export default function DescriptionEditor({ taskId }: { taskId: number }) {
    const docRef = useRef<Y.Doc>(new Y.Doc());
    const session = authClient.useSession();
    const providerRef = useRef<HocuspocusProvider | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isProviderReady, setIsProviderReady] = useState(false);
    const editorRef = useRef<Editor | null>(null);
    const isEmptyRef = useRef(true);
    const [connectionError, setConnectionError] = useState<string | null>(null);
    const { change: changeTaskProperty } = useChangeTaskProperty();
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [lightboxSlides, setLightboxSlides] = useState<ImageLightboxSlide[]>([]);
    const editorWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const wrapper = editorWrapperRef.current;
        if (!wrapper) return;

        const handleImageClick = (e: Event) => {
            const detail = (e as CustomEvent).detail as { src: string };
            const allImages = Array.from(wrapper.querySelectorAll(".orsanemedia--image img")) as HTMLImageElement[];
            const slides = allImages
                .map((el) => {
                    const anchor = el.closest("a");
                    return {
                        src: anchor?.href || el.src,
                        title: el.alt || undefined,
                        thumbSrc: el.currentSrc || el.src,
                    };
                })
                .filter((s) => !s.src.startsWith("blob:"));

            const index = slides.findIndex((s) => s.src === detail.src);

            setLightboxSlides(slides);
            setLightboxIndex(Math.max(0, index));
            setLightboxOpen(true);
        };

        wrapper.addEventListener("orsane-image-click", handleImageClick);
        return () => wrapper.removeEventListener("orsane-image-click", handleImageClick);
    }, []);

    const handleFileUpload = async (view: EditorView, file: File, position?: number) => {
        if (!editorRef.current) {
            throw new Error("Editor not properly initialized");
        }
        // Create a temporary preview URL and insert it
        const previewUrl = URL.createObjectURL(file);
        const fileName = file.name;
        const contentType = file.type;

        // If position is provided, set cursor there, otherwise use current position
        if (position !== undefined) {
            editor.commands.setTextSelection(position);
        }

        // Insert the media at the cursor position
        editorRef.current?.commands.addOrsaneMedia({
            url: previewUrl,
            fileName,
            contentType,
            id: "temp-id",
            href: "about:blank",
        });

        // Now upload the image to the server
        const {
            url,
            id,
            fileName: finalFileName,
        } = await trpcClient.upload.getPresignedTaskFileUrl.query({ contentType: file.type, taskId });
        await axios.put(url, file, {
            headers: {
                "Content-Type": file.type,
            },
        });
        const finalUrl = taskFileUrl({ taskId, filename: finalFileName, contentType });

        // Update all orsanemedia nodes that have the preview URL with the final URL
        editorRef.current?.state.doc.descendants((node: ProseMirrorNode, pos: number) => {
            if (node.type.name === "orsanemedia" && node.attrs.url === previewUrl) {
                if (!editor) {
                    console.error("Editor not found");
                    return;
                }
                // Create a transaction to update the node
                const { tr } = editorRef.current!.state;

                // Create new attributes by spreading the existing ones and updating the url
                const newAttrs = {
                    ...node.attrs,
                    url: finalUrl, // Replace with your final URL
                    href: taskFileUrl({ taskId, filename: finalFileName, contentType, fullWidth: true }),
                    id: id,
                };

                // Set the new attributes at the node's position
                tr.setNodeMarkup(pos, null, newAttrs);

                // Dispatch the transaction to update the editor
                view.dispatch(tr);
            }
        });
    };

    const handleDrop = async (view: any, event: DragEvent, file: File) => {
        // Get the position where the drop occurred
        const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });

        if (coordinates) {
            // Use setTimeout to ensure the editor has focus and the drop event is complete
            await promiseSleep(0);
            await handleFileUpload(view, file, coordinates.pos);
        }

        return true;
    };

    const editor = useEditor(
        {
            extensions: [
                StarterKit.configure({
                    // The Collaboration extension comes with its own history handling
                    undoRedo: false,
                    blockquote: false,
                    code: false,
                    horizontalRule: false,
                    link: {
                        autolink: false,
                        openOnClick: true,
                    },
                }),
                OrsaneMediaNode.configure(),
                Collaboration.configure({
                    document: docRef.current, // Configure Y.Doc for collaboration
                }),
                // Only add CollaborationCaret when provider is ready
                ...(isProviderReady && session?.data?.user && providerRef.current
                    ? [
                          CollaborationCaret.configure({
                              provider: providerRef.current,
                              user: {
                                  name: session?.data?.user.name,
                                  color: "#2563eb",
                                  avatar: avatarUrl(session?.data?.user.image, "40"),
                              },
                              render: (user) => {
                                  const cursor = document.createElement("span");
                                  cursor.classList.add("collaboration-carets__caret");

                                  const label = document.createElement("div");
                                  label.classList.add("collaboration-carets__label");
                                  label.insertBefore(document.createTextNode(user.name), null);

                                  cursor.insertBefore(label, null);

                                  requestAnimationFrame(() => {
                                      const parent = cursor.closest(".taskform-editor");
                                      const rect = cursor.getBoundingClientRect();

                                      const top = rect.top - (parent?.getBoundingClientRect().top ?? 0);

                                      if (top < 20) {
                                          // too close to top → place below
                                          label.classList.add("below");
                                      } else {
                                          label.classList.remove("below");
                                      }
                                  });

                                  return cursor;
                              },
                          }),
                      ]
                    : []),
                Emoji.configure({
                    enableEmoticons: true,
                    suggestion: false,
                }),
                Mention.extend(mentionListExtension()).configure(mentionListConfiguration()),
            ],
            onCreate: ({ editor }) => {
                editorRef.current = editor;
            },
            onUpdate: (e) => {
                throttle(e.editor);
            },
            editorProps: {
                handleDrop: (view: EditorView, event: DragEvent, _slice: any, moved: boolean) => {
                    if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
                        // if dropping external files
                        const file = event.dataTransfer.files[0]; // the dropped file
                        const filesize = Number((file.size / 1024 / 1024).toFixed(4)); // get the filesize in MB

                        if (filesize < 10) {
                            handleDrop(view, event, file).then(() => {
                                console.log("DONE DROPPING");
                            });

                            return true;
                        } else {
                            toast.error("Unsuccessful", {
                                description: `Files need to be less than 5mb in size.`,
                            });
                        }
                    }
                    return false;
                },
                handlePaste: (view: EditorView, event: ClipboardEvent, _slice: any) => {
                    const items = event.clipboardData?.items;
                    if (!items) return false;

                    // Look for image files in clipboard
                    for (let i = 0; i < items.length; i++) {
                        const item = items[i];
                        if (item.type.indexOf("image") === 0) {
                            const file = item.getAsFile();
                            if (file) {
                                const filesize = Number((file.size / 1024 / 1024).toFixed(4)); // get the filesize in MB

                                if (filesize < 10) {
                                    // Generate a filename for pasted images
                                    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
                                    const extension = file.type.split("/")[1] || "png";
                                    const generatedFile = new File([file], `pasted-image-${timestamp}.${extension}`, {
                                        type: file.type,
                                    });

                                    handleFileUpload(view, generatedFile).then(() => {
                                        console.log("DONE PASTING");
                                    });

                                    // Prevent default paste behavior
                                    event.preventDefault();
                                    return true;
                                } else {
                                    toast.error("Unsuccessful", {
                                        description: `Files need to be less than 5mb in size.`,
                                    });
                                    event.preventDefault();
                                    return true;
                                }
                            }
                        }
                    }

                    // If no image found, let default paste behavior handle it
                    return false;
                },
            },
        },
        [isProviderReady, session]
    );

    /**
     * Check if there is content in the document, and update the flag (used by indicator icons.)
     */
    const handleContentUpdated = useCallback(
        (ed: Editor) => {
            if (!ed) return;
            const json = ed.getJSON();
            const isEmpty = isTiptapDocumentEmpty(json);
            if (isEmpty === isEmptyRef.current) return;
            isEmptyRef.current = isEmpty;
            changeTaskProperty(taskId, { hasDescription: !isEmpty });
        },
        [changeTaskProperty, taskId]
    );

    const throttle = useThrottle(handleContentUpdated, 1000);

    useEffect(() => {
        // Clean up previous provider if it exists
        if (providerRef.current) {
            providerRef.current.disconnect();
            providerRef.current.destroy();
            providerRef.current = null;
        }

        // Create new provider for the current taskId
        providerRef.current = new HocuspocusProvider({
            url: `${import.meta.env.VITE_WS_BASE_URL}/hocuspocus`,
            name: `taskdescription-${taskId}`,
            document: docRef.current,
            onDisconnect: (p) => {
                console.log("Hocuspocus connection closed", p);
                setIsConnected(false);
            },
            onSynced: () => {
                setIsConnected(true);
            },
            onAuthenticationFailed: (data) => {
                console.error("Hocuspocus authentication failed:", data);
                setConnectionError("Authentication failed. Please refresh and try again or contact support.");
                setIsConnected(false);
            },
            onClose: () => {
                console.log("Hocuspocus connection closed");
                setIsConnected(false);
            },
            onStatus: ({ status }) => {
                // Handle various connection states
                if (status === "disconnected") {
                    setIsConnected(false);
                } else if (status === "connected") {
                    // Connection established but not yet synced
                    setConnectionError(null);
                }
            },
        });

        setIsProviderReady(true);

        // Cleanup function
        return () => {
            if (providerRef.current) {
                providerRef.current.disconnect();
                providerRef.current.destroy();
                providerRef.current = null;
            }
        };
    }, [taskId]);

    const insertMedia = useCallback(
        async (file: File) => {
            if (!editor) return;

            // For now we'll use a local URL, but in production this should be replaced
            // with the actual uploaded image URL from onImageUpload
            const url = URL.createObjectURL(file);
            const fileName: string = file.name;

            const contentType = file.type;

            editor.commands.addOrsaneMedia({ url, fileName, contentType, id: "temp-id", href: undefined });

            (editor as any).insertMedia = insertMedia;
        },
        [editor]
    );

    if (connectionError) {
        return (
            <div className="flex items-center justify-center h-full p-4">
                <div className="text-center max-w-md">
                    <div className="text-red-600 font-medium mb-2">Unable to load editor</div>
                    <div className="text-neutral-600 text-sm mb-4">{connectionError}</div>
                    <Button
                        nativeButton={false}
                        render={<a href="mailto:support@orsane.app">Contact support</a>}
                        variant="outline"
                    ></Button>
                </div>
            </div>
        );
    }

    const isReadOnly = !isConnected;

    return (
        <>
            {isConnected && (
                <BubbleMenu
                    updateDelay={200}
                    className="bg-white rounded-lg p-1 shadow border border-neutral-200 flex gap-0.5 items-center"
                    editor={editor}
                >
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor!.chain().focus().setHeading({ level: 1 }).run()}
                        className={cn(["h-8 w-8", { "bg-neutral-100": editor!.isActive("heading", { level: 1 }) }])}
                    >
                        <Heading className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor!.chain().focus().setParagraph().run()}
                        className={cn(["h-8 w-8", { "bg-neutral-100": editor!.isActive("paragraph") }])}
                    >
                        <Pilcrow className="h-4 w-4" />
                    </Button>
                    <BubbleSeparator />
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor!.chain().focus().toggleBold().run()}
                        className={cn(["h-8 w-8", { "bg-neutral-100": editor!.isActive("bold") }])}
                    >
                        <Bold className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor!.chain().focus().toggleItalic().run()}
                        className={cn(["h-8 w-8", { "bg-neutral-100": editor!.isActive("italic") }])}
                    >
                        <Italic className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor!.chain().focus().toggleStrike().run()}
                        className={cn(["h-8 w-8", { "bg-neutral-100": editor!.isActive("strike") }])}
                    >
                        <Strikethrough className="h-4 w-4" />
                    </Button>
                    <BubbleSeparator />
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor!.chain().focus().toggleBulletList().run()}
                        className={cn([
                            "h-8 w-8",
                            {
                                "bg-neutral-100": editor!.isActive("bulletList"),
                            },
                        ])}
                        aria-label="Toggle bullet list"
                    >
                        <List className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor!.chain().focus().toggleOrderedList().run()}
                        className={cn([
                            "h-8 w-8",
                            {
                                "bg-neutral-100": editor!.isActive("orderedList"),
                            },
                        ])}
                        aria-label="Toggle ordered list"
                    >
                        <ListOrdered className="h-4 w-4" />
                    </Button>
                </BubbleMenu>
            )}

            <div ref={editorWrapperRef} className="h-full">
                <EditorContent
                    readOnly={isReadOnly}
                    onKeyDown={(e) => {
                        if (e.key === "Tab") {
                            e.stopPropagation();
                        }
                    }}
                    className={cn(
                        "prose description-editor taskform-editor max-w-none focus-visible:outline-none h-full",
                        {
                            "opacity-30 pointer-events-none": isReadOnly,
                        }
                    )}
                    editor={editor}
                />
            </div>
            <ImageLightbox
                open={lightboxOpen}
                index={lightboxIndex}
                slides={lightboxSlides}
                onClose={() => setLightboxOpen(false)}
            />
        </>
    );
}
